var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { ReflectUtils } from "@dooboostore/core";
import { buildSwcParameterArgs, buildCommonKindValues } from "./parameter.js";
const MUTATION_OBSERVER_METADATA_KEY = Symbol.for("simple-web-component:mutation-observer");
const applyMutationObserver = (selector, options, targetObj, propertyKey) => {
  const constructor = targetObj.constructor;
  let observers = ReflectUtils.getOwnMetadata(MUTATION_OBSERVER_METADATA_KEY, constructor);
  if (!observers) {
    observers = [];
    ReflectUtils.defineMetadata(MUTATION_OBSERVER_METADATA_KEY, observers, constructor);
  }
  observers.push({ propertyKey, selector, options });
};
const resolveMutationObserverArgs = (selectorOrOptions, maybeOptions, extra) => {
  if (typeof selectorOrOptions === "string" || typeof selectorOrOptions === "function") {
    return { selector: selectorOrOptions, options: { ...maybeOptions ?? {}, ...extra } };
  }
  return { selector: "$this", options: { ...selectorOrOptions ?? {}, ...extra } };
};
const dispatchMutationObserver = (selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, extra) => {
  if ((typeof maybeOptionsOrPropertyKey === "string" || typeof maybeOptionsOrPropertyKey === "symbol") && descriptor !== void 0) {
    applyMutationObserver("$this", { subtree: true, ...extra }, selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey);
    return;
  }
  const { selector, options } = resolveMutationObserverArgs(
    selectorOrOptionsOrTarget,
    maybeOptionsOrPropertyKey,
    extra
  );
  return (targetObj, propertyKey) => {
    applyMutationObserver(selector, options, targetObj, propertyKey);
  };
};
function mutationObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchMutationObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, {});
}
function mutationObserverDelegateLight(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchMutationObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "light", delegate: true });
}
function mutationObserverDelegateShadow(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchMutationObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "shadow", delegate: true });
}
function mutationObserverDelegateAll(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchMutationObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "all", delegate: true });
}
function mutationObserverDelegate(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchMutationObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "auto", delegate: true });
}
function mutationObserverLight(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchMutationObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "light" });
}
function mutationObserverShadow(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchMutationObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "shadow" });
}
function mutationObserverAll(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor) {
  return dispatchMutationObserver(selectorOrOptionsOrTarget, maybeOptionsOrPropertyKey, descriptor, { root: "all" });
}
function getMutationObserverMetadata(target) {
  const constructor = typeof target === "function" ? target : target.constructor;
  return ReflectUtils.findAllMetadata(MUTATION_OBSERVER_METADATA_KEY, constructor).flat();
}
import { inObserverScope } from "../types/index.js";
import { SwcUtils } from "../utils/Utils.js";
class MutationObserverLifeCycler {
  constructor() {
    __publicField(this, "removeObserverCallbacksMap", /* @__PURE__ */ new WeakMap());
  }
  onConnected(helperHostSet, _set, scope) {
    const inst = helperHostSet.$this;
    const currentWin = helperHostSet.$w;
    const removeObserverCallbacks = scope === "rest" && this.removeObserverCallbacksMap.get(inst) || [];
    this.removeObserverCallbacksMap.set(inst, removeObserverCallbacks);
    const allMeta = (getMutationObserverMetadata(inst) ?? []).filter((m) => inObserverScope(m, scope));
    if (allMeta.length === 0) return;
    const shadowMetas = allMeta.filter((m) => {
      const r = m.options.root || "auto";
      return inst.shadowRoot && (r === "shadow" || r === "all" || r === "auto");
    });
    const lightMetas = allMeta.filter((m) => {
      const r = m.options.root || "auto";
      return r === "light" || r === "all" || !inst.shadowRoot && r === "auto";
    });
    const buildSet = (root, metas) => {
      if (metas.length === 0) return null;
      const initFrom = (o) => {
        const init = {
          childList: o.childList,
          attributes: o.attributes,
          characterData: o.characterData,
          subtree: o.subtree,
          attributeFilter: o.attributeFilter,
          attributeOldValue: o.attributeOldValue,
          characterDataOldValue: o.characterDataOldValue
        };
        if (!init.childList && !init.attributes && !init.characterData) init.childList = true;
        return init;
      };
      const observedNodes = /* @__PURE__ */ new Map();
      const owns = (meta, m) => (observedNodes.get(meta) ?? []).some((o) => o === m.target || !!meta.options.subtree && o.contains(m.target));
      const callback = (mutations, obs) => {
        for (const meta of metas) {
          const { selector, options } = meta;
          let matchedEls = [];
          let records = mutations;
          const typeMatches = (m) => {
            const has = !!(options.childList || options.attributes || options.characterData);
            if (m.type === "childList") return has ? !!options.childList : true;
            if (m.type === "attributes") return !!options.attributes;
            if (m.type === "characterData") return !!options.characterData;
            return true;
          };
          const collect = (matchesSel, records2 = mutations) => records2.flatMap((m) => {
            if (!typeMatches(m)) return [];
            const els = [];
            if (matchesSel(m.target)) els.push(m.target);
            Array.from(m.addedNodes || []).filter(matchesSel).forEach((n) => els.push(n));
            Array.from(m.removedNodes || []).filter(matchesSel).forEach((n) => els.push(n));
            return els;
          });
          if (typeof selector === "string" && (selector === "$this" || selector === "")) {
            records = mutations.filter((m) => typeMatches(m) && owns(meta, m));
            matchedEls = records.length ? [inst] : [];
          } else if (options.delegate && typeof selector === "string") {
            matchedEls = collect((n) => {
              if (!n || n.nodeType !== 1) return false;
              const el = n;
              return el.matches?.(selector) || !!el.closest?.(selector);
            });
          } else if (typeof selector === "string") {
            records = mutations.filter((m) => owns(meta, m));
            matchedEls = records.length ? collect((n) => {
              if (!n || n.nodeType !== 1) return false;
              return n.matches?.(selector);
            }, records) : [];
          } else if (typeof selector === "function") {
            records = mutations.filter((m) => typeMatches(m) && owns(meta, m));
            matchedEls = (observedNodes.get(meta) ?? []).filter((o) => records.some((m) => o === m.target || !!options.subtree && o.contains(m.target)));
          } else {
            matchedEls = mutations.filter((m) => typeMatches(m)).map((m) => m.target).filter((t) => t && t.nodeType === 1);
          }
          if (matchedEls.length === 0) continue;
          void (async () => {
            const helper = SwcUtils.getHelperAndHostSet(inst);
            if (options.filter && !await options.filter(matchedEls, { currentThis: inst, helper })) return;
            const common = buildCommonKindValues(inst, helperHostSet.$w);
            const legacyArgs = [matchedEls, records, obs, { ...common.hostSet, $root: root }];
            const buildArgs = (beforeReturn) => buildSwcParameterArgs(inst, meta.propertyKey, {
              ...common,
              mutationObserverBeforeReturn: beforeReturn
            }, [...legacyArgs, beforeReturn]);
            let args = buildArgs(void 0);
            if (options.before) {
              const br = await options.before(matchedEls, { currentThis: inst, helper });
              args = buildArgs(br);
            }
            let result, error;
            try {
              result = await inst[meta.propertyKey](...args);
            } catch (e) {
              error = e;
            } finally {
              if (options.finally) await options.finally(matchedEls, { currentThis: inst, helper }, { args, result, error });
            }
            if (error) throw error;
          })().catch((e) => console.error("[SWC] mutationObserver handler error:", e));
        }
      };
      const observeTargets = [];
      if (metas.some((m) => m.options.delegate)) {
        observeTargets.push({ target: root, options: { childList: true, subtree: true } });
      }
      for (const m of metas.filter((m2) => !m2.options.delegate)) {
        const { selector, options } = m;
        const mInit = initFrom(options);
        if (typeof selector === "string") {
          const nodes = selector === "$this" || selector === "" ? [root] : Array.from(root.querySelectorAll(selector));
          for (const n of nodes) observeTargets.push({ target: n, options: mInit });
          observedNodes.set(m, nodes);
        } else if (typeof selector === "function") {
          const res = selector(inst, helperHostSet);
          let targets = [];
          if (res instanceof currentWin.HTMLElement) targets = [res];
          else if (res instanceof currentWin.NodeList) targets = Array.from(res).filter((e) => e instanceof currentWin.HTMLElement);
          else if (Array.isArray(res)) targets = res.filter((e) => e instanceof currentWin.HTMLElement);
          for (const el of targets) observeTargets.push({ target: el, options: mInit });
          observedNodes.set(m, targets);
        }
      }
      for (const m of metas) {
        if (m.options.removeObserver) removeObserverCallbacks.push({ fn: m.options.removeObserver, target: root, opts: m.options });
      }
      return { callback, observeTargets };
    };
    const shadowSet = inst.shadowRoot ? buildSet(inst.shadowRoot, shadowMetas) : null;
    const lightSet = buildSet(inst, lightMetas);
    const mutationObserverSet = [
      ...(shadowSet?.observeTargets ?? []).map((t) => ({ ...t, callback: shadowSet.callback })),
      ...(lightSet?.observeTargets ?? []).map((t) => ({ ...t, callback: lightSet.callback }))
    ];
    if (mutationObserverSet.length === 0) return;
    return { mutationObserverSet };
  }
  onDisconnected(helperHostSet) {
    const inst = helperHostSet.$this;
    for (const cb of this.removeObserverCallbacksMap.get(inst) ?? []) {
      try {
        cb.fn(cb.target, cb.opts);
      } catch (e) {
        console.error("[SWC] removeObserver error:", e);
      }
    }
    this.removeObserverCallbacksMap.delete(inst);
  }
}
export {
  MUTATION_OBSERVER_METADATA_KEY,
  MutationObserverLifeCycler,
  getMutationObserverMetadata,
  mutationObserver,
  mutationObserverAll,
  mutationObserverDelegate,
  mutationObserverDelegateAll,
  mutationObserverDelegateLight,
  mutationObserverDelegateShadow,
  mutationObserverLight,
  mutationObserverShadow
};
//# sourceMappingURL=mutationObserver.js.map
